import React, { useEffect, useState } from "react";
import { PageHeader, Toast, PasswordInput } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { lock } from "@/lib/perm";
import ViewOnly from "@/components/ViewOnly";

const EMPTY = { host: "", port: 587, username: "", password: "", from_address: "", send_access_email: false };

export default function SmtpSettings() {
  const { user, has } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/smtp-config").then(r => {
      setCfg(r.data);
      setForm({ host: r.data.host, port: r.data.port, username: r.data.username, password: "", from_address: r.data.from_address, send_access_email: !!r.data.send_access_email });
      setTestTo(t => t || user?.email || "");
    });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cfg) return <div style={{ padding: 40 }}>Se încarcă...</div>;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/smtp-config", {
        host: form.host.trim(), port: Number(form.port) || 587, username: form.username.trim(),
        password: form.password || null, from_address: form.from_address.trim(),
        send_access_email: !!form.send_access_email,
      });
      setCfg(r.data);
      setForm(f => ({ ...f, password: "" }));
      setMsg({ text: "Configurare SMTP salvată", type: "success" });
      setTimeout(() => setMsg({ text: "" }), 2000);
    } catch (e) {
      setMsg({ text: e?.response?.data?.detail || "Eroare la salvare", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/smtp-config/test", {
        host: form.host.trim(), port: Number(form.port) || 587, username: form.username.trim(),
        password: form.password || null, from_address: form.from_address.trim(), to: testTo.trim(),
      });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || "Eroare la trimiterea testului" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <PageHeader title="Email (SMTP)" subtitle="Configurarea serverului de email folosit pentru notificări — proprie acestui angajator, salvată criptat" />
      <Toast msg={msg.text} type={msg.type} />

      <ViewOnly on={!has("smtp.update")}>
      <div className="card fade-up" style={{ padding: 24, maxWidth: 560 }}>
        <div style={{ display: "grid", gap: 14 }}>
          <div><label className="label label-req">Server SMTP (host)</label>
            <input className="input" value={form.host} onChange={e => setForm({ ...form, host: e.target.value })}
              placeholder="ex: smtp.gmail.com" data-testid="smtp-host" /></div>
          <div className="grid-2">
            <div><label className="label label-req">Port</label>
              <input className="input" type="number" value={form.port} onChange={e => setForm({ ...form, port: e.target.value })} data-testid="smtp-port" /></div>
            <div><label className="label">Utilizator</label>
              <input className="input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                placeholder="ex: firma@gmail.com" data-testid="smtp-username" /></div>
          </div>
          <div><label className="label">Parolă {cfg.password_set && <span style={{ color: "#64748B", fontWeight: 400 }}>(deja setată — completează doar dacă vrei s-o schimbi)</span>}</label>
            <PasswordInput autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
              placeholder={cfg.password_set ? "••••••••" : "parolă sau parolă de aplicație Google"} testId="smtp-password" />
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>
              Pentru Gmail/Google Workspace, cu verificarea în doi pași activată, folosește o <b>parolă de aplicație</b> generată din contul Google — nu parola obișnuită.
            </div>
          </div>
          <div><label className="label">Adresă „De la"</label>
            <input className="input" value={form.from_address} onChange={e => setForm({ ...form, from_address: e.target.value })}
              placeholder="ex: noreply@firma.ro" data-testid="smtp-from" /></div>
          <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 14 }}>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" checked={!!form.send_access_email} onChange={e => setForm({ ...form, send_access_email: e.target.checked })}
                style={{ marginTop: 3 }} data-testid="smtp-send-access-email" />
              <span><b>Trimite pe e-mail datele de acces</b>
                <span style={{ display: "block", fontSize: 12, color: "#64748B", fontWeight: 400 }}>
                  Când unei persoane i se acordă acces în platformă (utilizator nou cu acces sau „Acordă acces"), ea primește un e-mail cu
                  utilizatorul și parola temporară. Persoanele fără adresă de e-mail nu pot primi acces cât timp opțiunea e bifată.
                </span>
              </span>
            </label>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button className="btn btn-primary" onClick={save} data-testid="save-smtp" {...lock(!saving && has("smtp.update"))}>
            {saving ? "Se salvează..." : "Salvează"}
          </button>
        </div>
      </div>
      </ViewOnly>

      <div className="card fade-up" style={{ padding: 24, maxWidth: 560, marginTop: 18 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Trimite email de test</h3>
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>
          Testează datele completate mai sus (chiar dacă nu ai apăsat încă „Salvează").
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label className="label">Trimite la</label>
            <input className="input" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="adresa@exemplu.ro" data-testid="smtp-test-to" />
          </div>
          <button className="btn btn-secondary" onClick={sendTest} data-testid="send-test-email" {...lock(!testing && has("smtp.update"))}>
            {testing ? "Se trimite..." : "Trimite email de test"}
          </button>
        </div>
        {testResult && (
          <div style={{
            marginTop: 14, padding: "10px 14px", borderRadius: 6, fontSize: 13,
            background: testResult.ok ? "#F0FDF4" : "#FEF2F2",
            color: testResult.ok ? "#166534" : "#991B1B",
            border: `1px solid ${testResult.ok ? "#BBF7D0" : "#FECACA"}`,
          }} data-testid="smtp-test-result">
            {testResult.ok ? "✓ " : "✗ "}{testResult.message}
          </div>
        )}
      </div>
    </>
  );
}
